import type { FieldTree } from '@angular/forms/signals';
import type { SelectedFileLegacy } from '@sites/entity-core';
import type { ImageForm } from '@sites/entity-signal-form';
import type { SelectedFileInterface } from '@sendpulse/file-manager';
import type { SelectedFileExtended } from '@common/types/file.type';
import type { ReplacedFile } from '@components/sp-file-manager/src/types';

import { ChangeDetectionStrategy, Component, computed, inject, input, output, signal } from '@angular/core';

import { CSS_RESTRICTIONS } from '@common/constants';
import { URL as URL_REGEX } from '@common/regexp';
import { extendWebFileDataPromise } from '@common/helpers';
import { EConditionType, EFontStyle, EMaskShapeType, EOverlayType } from '@common/enums';
import { DirectoryItemCategory, FmSourceType } from '@sendpulse/file-manager';

import { ERadioGroupType } from '@ui/sp-radio-group';
import { ThemeStateService } from '@sites/site-theme/src/state';

import { environment } from '@web-env/environment';
import { CommonElementsModule } from '@web/widgets/common/common-elements.module';
import { SpFileManagerComponentModule } from '@components/sp-file-manager';
import { WebColorPickerComponent } from '@web/shared/components/web-color-picker/web-color-picker.component';

/**
 * Image-effect sub-editor (overlay / text / mask / link panel) for the `image`
 * widget. Fully signal-based: it receives the widget's {@link ImageForm} and the
 * template binds straight to the picked file's nested fields off {@link imageField}
 * (`imageField.<field>().value()` to read, `…().value.set(…)` to write), so every
 * edit writes through to the live form — no detached FormGroup, no DTO round-trip.
 * Only edits that carry real logic (link normalisation, the condition / overlay
 * cascades, "set all paddings", text-style toggling, file picking) keep a method;
 * cascades run inside `form.transact` so they land as a single history entry.
 *
 * The parent (`web-image-control`) opens this panel after snapshotting the
 * current picked file. `saveEvent` keeps the live edits and closes; `cancelEvent`
 * asks the parent to restore that snapshot.
 */
@Component({
    selector: 'web-image-settings',
    templateUrl: './image-settings.component.html',
    styleUrls: ['./image-settings.component.less'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [CommonElementsModule, SpFileManagerComponentModule, WebColorPickerComponent],
})
export class ImageSettingsComponent {
    public readonly form = input.required<ImageForm>();

    /** Keep the live edits and close the panel (footer "Save" + breadcrumb back). */
    public readonly saveEvent = output<void>();
    /** Revert to the snapshot taken when the panel opened, then close (footer "Cancel"). */
    public readonly cancelEvent = output<void>();

    private readonly themeState = inject(ThemeStateService);

    public readonly FONT_SIZE_MAX = 72;
    public readonly CSS_RESTRICTIONS = CSS_RESTRICTIONS;
    public readonly EFontStyle = EFontStyle;
    public readonly EOverlayType = EOverlayType;
    public readonly EConditionType = EConditionType;
    public readonly ERadioGroupType = ERadioGroupType;
    public readonly EMaskShapeType = EMaskShapeType;
    public readonly FmSourceType = FmSourceType;
    public readonly DirectoryItemCategory = DirectoryItemCategory;

    public readonly selectedThemeColors = this.themeState.selectedColors;

    public readonly buttonDisabled = signal<boolean>(false);
    /** Set on link blur — the validation hint only shows after the field is touched. */
    public readonly linkTouched = signal<boolean>(false);

    /**
     * The picked-file FieldTree. `data.image` is `SelectedFile | null` on the
     * schema, so the form's `image` accessor is typed nullable and TS hides the
     * child fields; this panel only renders with a picked file present, so we
     * narrow once here and the template reads children straight off it.
     */
    public get imageField(): FieldTree<SelectedFileLegacy> {
        return this.form().image as unknown as FieldTree<SelectedFileLegacy>;
    }

    public readonly linkInvalid = computed<boolean>(() => {
        const value = this.imageField.link().value();
        return value ? !URL_REGEX.test(value) : false;
    });

    /**
     * Mirrors the legacy FormGroup `valueChanges` rule: bare hosts get an
     * `https://` prefix unless they already carry a known protocol.
     */
    public setLink(value: string): void {
        const validProtocolsRegex = /^(mailto:|ftp:|tel:|file:)/;
        const normalized =
            value && !value.startsWith('http://') && !value.startsWith('https://') && !validProtocolsRegex.test(value)
                ? `https://${value}`
                : value;

        this.imageField.link().value.set(normalized);
    }

    public setCondition(value: EConditionType): void {
        this.form().transact(() => {
            this.imageField.condition().value.set(value);
            const overlay = this.imageField.overlay().value();

            if (value === EConditionType.ON_HOVER && overlay === EOverlayType.MASK) {
                this.applyOverlay(null);
            }

            if (value === EConditionType.ALWAYS && overlay === EOverlayType.IMAGE) {
                this.imageField.overlayImage().value.set(null);
            }
        });
    }

    public setOverlay(value: EOverlayType | null): void {
        this.form().transact(() => this.applyOverlay(value));
    }

    /**
     * Shared overlay-change cascade (mirrors the legacy `overlay.valueChanges`):
     * mask gets a default shape, every other overlay clears the mask, and the
     * text / image overlays are mutually reset. Always invoked inside a
     * transaction by its callers.
     */
    private applyOverlay(value: EOverlayType | null): void {
        this.imageField.overlay().value.set(value);
        this.imageField.maskShape().value.set(value === EOverlayType.MASK ? EMaskShapeType.SHAPE_1 : null);
        this.imageField.description().value.set(null);
        this.imageField.overlayImage().value.set(null);
    }

    public isActiveTextStyle(fontStyle: EFontStyle): boolean {
        return (this.imageField.textStyle().value() ?? []).includes(fontStyle);
    }

    public toggleActiveTextStyle(fontStyle: EFontStyle): void {
        this.imageField.textStyle().value.update((current) => {
            const styles = current ?? [];
            return styles.includes(fontStyle) ? styles.filter((el) => el !== fontStyle) : [...styles, fontStyle];
        });
    }

    public setAllPaddings(value: number): void {
        this.form().transact(() => {
            this.imageField.paddingTop().value.set(value);
            this.imageField.paddingBottom().value.set(value);
            this.imageField.paddingLeft().value.set(value);
            this.imageField.paddingRight().value.set(value);
        });
    }

    public async setImage(file: SelectedFileInterface): Promise<void> {
        this.buttonDisabled.set(true);
        const extendedFile: SelectedFileExtended = await extendWebFileDataPromise(file, environment.fileManagerCdnUrl);
        this.imageField.overlayImage().value.set(extendedFile as unknown as SelectedFileLegacy['overlayImage']);
        this.buttonDisabled.set(false);
    }

    public async replaceImage({ file }: ReplacedFile): Promise<void> {
        this.buttonDisabled.set(true);
        const extendedFile: SelectedFileExtended = await extendWebFileDataPromise(file, environment.fileManagerCdnUrl);
        this.imageField.overlayImage().value.set(extendedFile as unknown as SelectedFileLegacy['overlayImage']);
        this.buttonDisabled.set(false);
    }

    public onSave(): void {
        this.saveEvent.emit();
    }

    public onReset(): void {
        this.cancelEvent.emit();
    }
}
